/* eslint-disable max-lines-per-function */
import { Add } from '@mui/icons-material';
import { addTaskListItemSchema } from '../../schema/tasks_schema';
import { AddTasksListItemParams } from '../../params/redux/task';
import CardWrapper from '../wrapper/card';
import ColorPickerComponent from '../reusable/color_picker';
import { CustomerObject } from '../../params/redux/customer';
import CustomInput from '../reusable/input';
import { fetchClientsCustomerThunk } from '../../redux/thunks/customer';
import LabelWrapper from '../wrapper/label';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../redux/store';
import { Avatar, AvatarGroup, Box, Button, Popover, Tooltip, Typography } from '@mui/material';
import { brand, gray } from '../../theme/themePrimitives';
import { createTaskListItemThunk, editTaskListItemThunk } from '../../redux/thunks/task';
import { Form, Formik } from 'formik';
import { settingAddingTask, settingSelectedBoardList } from '../../redux/slices/task';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

const AddNewTaskComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>();
    const { selectedBoardList, loaders, selectedBoardTask } = useSelector((state: RootState) => state.task);
    const { customers } = useSelector((state: RootState) => state.customer);
    const { currentUser } = useSelector((state: RootState) => state.app);
    const initialValues: AddTasksListItemParams = {
        list_id: selectedBoardList?.id || 0,
        item_id: selectedBoardTask?.id || 0,
        title: selectedBoardTask?.title || '',
        description: selectedBoardTask?.description || '',
        due_date: selectedBoardTask?.due_date || '',
        due_time: selectedBoardTask?.due_time || '12:00',
        labels: selectedBoardTask?.label || '',
        label_color_tag: selectedBoardTask?.label_color_tag || 'danger',
        assignees: selectedBoardTask?.members?.map((member) => member.member_id) || []
    };

    const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        const minDate = `${yyyy}-${mm}-${dd}`;

    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                await dispatch(fetchClientsCustomerThunk(id ? parseInt(id as string, 10) : 0)).unwrap();
            } catch (e) {
                //
            }
        };
        fetchCustomers();
    }, []);

    const handleSubmit = async (values: AddTasksListItemParams) => {
        // Handle form submission
        try {
            if (selectedBoardTask?.id) {
                await dispatch(editTaskListItemThunk({ data: values, client_id: id ? parseInt(id as string, 10) : 0 })).unwrap();
            } else {
                await dispatch(createTaskListItemThunk({ data: values, client_id: id ? parseInt(id as string, 10) : 0 })).unwrap();
            }
            dispatch(settingAddingTask(false));
            dispatch(settingSelectedBoardList(null));
        } catch (e) {
            // Catch errors
        }
    };

    const cancelHandler = () => {
        dispatch(settingAddingTask(false));
        dispatch(settingSelectedBoardList(null));
    };

    return <CardWrapper>
        <Typography variant="h6" color="textSecondary" mb={2}>{selectedBoardTask?.id ? 'Edit Task' : 'Add New Task'}</Typography>
        <Formik
            initialValues={initialValues}
            validationSchema={addTaskListItemSchema}
            onSubmit={handleSubmit}
        >
            {({ values, handleChange, handleBlur, errors, touched, setFieldValue }) => (
                <Form>
                    <Box display="flex" gap="10px">
                        <CustomInput
                            value={values.title}
                            onChange={handleChange('title')}
                            onBlur={handleBlur('title')}
                            error={touched.title && errors.title}
                            fullWidth
                            label="Title"
                            placeholder="Enter title"
                            variant="outlined"
                            disabled={loaders.isCreating || loaders.isUpdating}
                        />
                        <CustomInput
                            type="date"
                            value={values.due_date}
                            onChange={handleChange('due_date')}
                            onBlur={handleBlur('due_date')}
                            error={touched.due_date && errors.due_date}
                            fullWidth
                            label="Due Date"
                            placeholder="1/10/2025"
                            variant="outlined"
                            disabled={loaders.isCreating || loaders.isUpdating}
                            min={minDate}
                        />
                        <CustomInput
                            type="time"
                            value={values.due_time}
                            onChange={handleChange('due_time')}
                            onBlur={handleBlur('due_time')}
                            error={touched.due_time && errors.due_time}
                            fullWidth
                            label="Due Time"
                            placeholder="12:00"
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true
                              }}
                              inputProps={{
                                step: 300
                              }}
                              ampm={false}
                            disabled={loaders.isCreating || loaders.isUpdating}
                        />
                        <Box display="none" width="100%">
                            <LabelWrapper label="Assigned Users" />
                            <Box display="flex" gap={1}>
                                {values.assignees?.length > 0 && (
                                    <AvatarGroup max={10}>
                                        {values.assignees?.map((assigneeId) => (
                                            <Tooltip key={assigneeId} title={assigneeId}>
                                                <Avatar src={assigneeId.toString()} sx={{ width: 30, height: 30, bgcolor: gray[300], color: 'white' }}>
                                                </Avatar>
                                            </Tooltip>
                                        ))}
                                    </AvatarGroup>
                                )}
                                {/* Add Assignee Button */}
                                <Box bgcolor={brand[300]} borderRadius="20px" height={30} width={30} display="flex" justifyContent="center" alignItems="center" color="white" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ cursor: 'pointer' }}>
                                    <Add />
                                </Box>
                            </Box>
                            <Popover
                                open={Boolean(anchorEl)}
                                anchorEl={anchorEl}
                                onClose={() => setAnchorEl(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            >
                                <Box p={2} width="250px">
                                    {customers.filter((customer: CustomerObject) => customer.id !== currentUser?.id && customer.is_primary !== 1).map((member) => (
                                        <Box
                                            key={member.id}
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            onClick={() => {
                                                const updatedAssignees = values.assignees.includes(member.id)
                                                    ? values.assignees.filter((assigneeId) => assigneeId !== member.id)
                                                    : [...values.assignees, member.id];
                                                setFieldValue('assignees', updatedAssignees);
                                            }}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <Typography variant="subtitle2" color="textDisabled" fontWeight="bold" >{member.first_name} {member.last_name}</Typography>
                                            <Button
                                                variant="text"
                                                color={values.assignees?.some((r: number) => (r === member.id)) ? 'error' : 'primary'}>
                                                {values.assignees?.some((r: number) => (r === member.id)) ? 'Remove' : 'Add'}
                                            </Button>
                                        </Box>
                                    ))}
                                </Box>
                            </Popover>
                        </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap="10px" my={2}>
                        <CustomInput
                            value={values.labels}
                            onChange={handleChange('labels')}
                            onBlur={handleBlur('labels')}
                            error={touched.labels && errors.labels}
                            fullWidth
                            label="Label"
                            placeholder="Enter label"
                            variant="outlined"
                            disabled={loaders.isCreating || loaders.isUpdating}
                        />
                        <Box width="100%">
                            <LabelWrapper label="Label Color Tag" />
                            <ColorPickerComponent
                                selected={values.label_color_tag}
                                onChange={(value: string) => setFieldValue('label_color_tag', value)}
                            />
                        </Box>
                    </Box>
                    <CustomInput
                        type="textarea"
                        rows={4}
                        value={values.description}
                        onChange={handleChange('description')}
                        onBlur={handleBlur('description')}
                        error={touched.description && errors.description}
                        fullWidth
                        label="Description"
                        placeholder="Enter Description"
                        variant="outlined"
                        disabled={loaders.isCreating || loaders.isUpdating}
                    />
                    <Box display="flex" justifyContent="end" gap="10px" mt={2}>
                        <Button variant="outlined" color="secondary" onClick={cancelHandler} disabled={loaders.isCreating || loaders.isUpdating}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={loaders.isCreating || loaders.isUpdating}>
                            {selectedBoardTask?.id && loaders.isUpdating ? 'Saving...' : selectedBoardTask?.id ? 'Save' : loaders.isCreating ? 'Creating...' : 'Create'}
                        </Button>
                    </Box>
                </Form>
            )}
        </Formik>
    </CardWrapper>;
};

export default AddNewTaskComponent;